import React, { useState } from 'react';
import {
  Play,
  ChevronDown,
  ChevronUp,
  Clock,
  Check,
  ArrowLeft,
  Paperclip,
  FileText,
  Download,
  ExternalLink,
  Share2,
} from 'lucide-react';
import { MentorAreaConfig, MentorModule, MentorLesson } from '../../types/mentorship';
import { useToast } from '../../contexts/ToastContext';

interface MentorshipLessonPageProps {
  config: MentorAreaConfig;
  currentModule: MentorModule;
  currentLesson: MentorLesson;
  onSelectLesson: (module: MentorModule, lesson: MentorLesson) => void;
  onToggleCompleteLesson: (lessonId: string) => void;
  onBackToCourse: () => void;
}

export const MentorshipLessonPage: React.FC<MentorshipLessonPageProps> = ({
  config,
  currentModule,
  currentLesson,
  onSelectLesson,
  onToggleCompleteLesson,
  onBackToCourse,
}) => {
  const { showToast } = useToast();
  const [expandedModuleIds, setExpandedModuleIds] = useState<string[]>([currentModule.id]);
  const [activeTab, setActiveTab] = useState<'sobre' | 'anexos'>('sobre');

  const handleShareMentorshipLesson = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?aula=${encodeURIComponent(currentModule.id)}&video=${encodeURIComponent(currentLesson.id)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: currentLesson.title,
          text: `Assista a aula "${currentLesson.title}" da mentoria na plataforma MasterClub!`,
          url: shareUrl,
        });
        showToast({
          type: 'success',
          title: 'Compartilhado!',
          message: 'Aula compartilhada com sucesso.',
        });
        return;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast({
        type: 'success',
        title: 'Link copiado!',
        message: 'O link desta aula foi copiado para a sua área de transferência.',
      });
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Erro ao copiar',
        message: 'Não foi possível copiar o link da aula.',
      });
    }
  };

  const toggleModuleAccordion = (moduleId: string) => {
    setExpandedModuleIds((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0] || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0`
      : url;
  };

  const embedUrl = getEmbedUrl(currentLesson.videoUrl);

  // Mock de Anexos para a aba de "Anexos"
  const mockAttachments = [
    { name: 'Material de Apoio em PDF.pdf', size: '2.4 MB', type: 'pdf' },
    { name: 'Planilha de Cálculo de Precificação.xlsx', size: '1.1 MB', type: 'excel' },
    { name: 'Link para Repositório de Modelos', url: 'https://github.com', type: 'link' },
  ];

  return (
    <div className="w-full min-h-screen bg-[#121316] text-white animate-fade-in select-none -mt-6 -mx-4 md:-mx-10 w-[calc(100%+2rem)] md:w-[calc(100%+5rem)] flex flex-col">
      {/* Top Header Navigation (Breadcrumb) */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-white/10 bg-[#121316]">
        <button
          onClick={onBackToCourse}
          className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para Módulos</span>
        </button>

        <div className="text-xs text-gray-400 truncate max-w-2xl">
          <span>{config.courseTitle}</span>
          <span className="mx-2 text-gray-600">›</span>
          <span className="text-gray-300">{currentModule.title}</span>
          <span className="mx-2 text-gray-600">›</span>
          <span className="text-[#0FFF55] font-semibold">{currentLesson.title}</span>
        </div>
      </div>

      {/* Main Grid: Left Column Player & Info (78%) | Right Column Flush Sidebar (22%) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 flex-1 w-full">
        {/* Left Column: Huge Video Player & Content Info (78% / col-span-9) */}
        <div className="lg:col-span-9 p-4 md:p-8 flex flex-col gap-6 w-full">
          {/* Video Player Box */}
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={currentLesson.title}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-3">
                <Play className="w-12 h-12 text-[#0FFF55]" />
                <p className="text-sm font-medium">Vídeo em processamento</p>
              </div>
            )}
          </div>

          {/* Lesson Header Title & Completion Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">
                {currentModule.title} • Conteúdo {currentLesson.order}
              </div>
              <h1 className="text-xl md:text-3xl font-black text-white uppercase tracking-tight">
                {currentLesson.title}
              </h1>
              <p className="text-xs text-gray-400 mt-1">{currentLesson.publishedAtText}</p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleShareMentorshipLesson}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-white/10 hover:bg-[#0FFF55]/15 hover:text-[#0FFF55] hover:border-[#0FFF55]/40 border border-white/15 text-gray-200 transition-all active:scale-95"
                title="Compartilhar link desta aula"
              >
                <Share2 className="w-4 h-4 text-gray-400 hover:text-[#0FFF55]" />
                <span>Compartilhar</span>
              </button>

              {/* Concluir Button */}
              <button
                onClick={() => onToggleCompleteLesson(currentLesson.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all active:scale-95 flex-shrink-0 ${
                  currentLesson.isCompleted
                    ? 'bg-emerald-500/20 text-[#0FFF55] border border-[#0FFF55]/40 hover:bg-emerald-500/30'
                    : 'bg-white/10 hover:bg-white/20 text-white border border-white/15'
                }`}
              >
                <Check className={`w-4 h-4 ${currentLesson.isCompleted ? 'text-[#0FFF55]' : 'text-gray-400'}`} />
                <span>{currentLesson.isCompleted ? 'Concluído' : 'Concluir'}</span>
              </button>
            </div>
          </div>

          {/* Tabs Navigation: "Sobre esta aula" & "Anexos" */}
          <div className="flex items-center gap-6 border-b border-white/10 pb-2 pt-2">
            <button
              onClick={() => setActiveTab('sobre')}
              className={`flex items-center gap-2 text-xs font-bold pb-2 border-b-2 transition-all ${
                activeTab === 'sobre'
                  ? 'border-[#0FFF55] text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Sobre esta aula</span>
            </button>

            <button
              onClick={() => setActiveTab('anexos')}
              className={`flex items-center gap-2 text-xs font-bold pb-2 border-b-2 transition-all ${
                activeTab === 'anexos'
                  ? 'border-[#0FFF55] text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              <Paperclip className="w-4 h-4" />
              <span>Anexos</span>
              <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-[10px] font-semibold text-gray-300">
                {mockAttachments.length}
              </span>
            </button>
          </div>

          {/* Tab Content 1: Sobre esta aula */}
          {activeTab === 'sobre' && (
            <div className="bg-[#17181c] p-6 rounded-2xl border border-white/10 text-xs text-gray-300 leading-relaxed font-light">
              {currentLesson.description ? (
                <p>{currentLesson.description}</p>
              ) : (
                <p className="text-gray-500 italic">Nenhuma descrição disponível para esta aula.</p>
              )}
            </div>
          )}

          {/* Tab Content 2: Anexos */}
          {activeTab === 'anexos' && (
            <div className="flex flex-col gap-3">
              {mockAttachments.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 rounded-xl bg-[#17181c] border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Paperclip className="w-4 h-4 text-[#0FFF55]" />
                    <div>
                      <h5 className="text-xs font-semibold text-white">{item.name}</h5>
                      {item.size && <span className="text-[10px] text-gray-400">{item.size}</span>}
                    </div>
                  </div>

                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-gray-200 transition-all">
                    {item.type === 'link' ? (
                      <>
                        <span>Acessar</span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                      </>
                    ) : (
                      <>
                        <span>Baixar</span>
                        <Download className="w-3.5 h-3.5 text-[#0FFF55]" />
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Accordion Drawer Sidebar Flush Aligned to Right Edge (Fidelidade ao Print 2 da Hubla) */}
        <div className="lg:col-span-3 bg-[#121316] border-l border-white/10 flex flex-col select-none w-full min-h-screen">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#17181c]">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider">
              Conteúdo do Curso
            </h3>
            <span className="text-xs font-semibold text-[#0FFF55]">
              {config.modules.length} Módulos
            </span>
          </div>

          {/* Accordion List of All Modules */}
          <div className="flex flex-col flex-1 divide-y divide-white/5">
            {config.modules.map((mod) => {
              const isExpanded = expandedModuleIds.includes(mod.id);
              const completedCount = mod.lessons.filter((l) => l.isCompleted).length;
              const modProgress =
                mod.lessons.length > 0
                  ? Math.round((completedCount / mod.lessons.length) * 100)
                  : 0;

              return (
                <div key={mod.id} className="flex flex-col">
                  {/* Module Header Toggle Button (Clean Hubla Style) */}
                  <button
                    onClick={() => toggleModuleAccordion(mod.id)}
                    className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-bold text-gray-400 flex-shrink-0">
                        {mod.order}
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-white uppercase truncate">
                          {mod.title}
                        </h4>
                        <span className="text-[10px] text-gray-400">
                          {mod.lessons.length} conteúdo(s)
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-shrink-0">
                      <span className="text-[10px] font-semibold text-gray-400">
                        {modProgress}%
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Lessons List Inside Expanded Module */}
                  {isExpanded && (
                    <div className="flex flex-col p-2 gap-2 bg-[#0d0e11]">
                      {mod.lessons.map((les) => {
                        const isCurrentLesson = les.id === currentLesson.id;

                        return (
                          <div
                            key={les.id}
                            onClick={() => onSelectLesson(mod, les)}
                            className={`p-3 rounded-xl flex items-center justify-between gap-3 cursor-pointer transition-all ${
                              isCurrentLesson
                                ? 'bg-[#1c1e24] border border-[#0FFF55]/40 shadow-lg'
                                : 'bg-[#121316] hover:bg-[#17181c] border border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3 overflow-hidden min-w-0">
                              {/* Thumbnail */}
                              <div className="relative w-16 h-10 rounded-lg overflow-hidden bg-black flex-shrink-0 border border-white/10">
                                <img
                                  src={les.thumbnailUrl}
                                  alt={les.title}
                                  className="w-full h-full object-cover"
                                />
                                {isCurrentLesson && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <Play className="w-4 h-4 text-[#0FFF55] fill-[#0FFF55]" />
                                  </div>
                                )}
                              </div>

                              <div className="truncate">
                                <h5
                                  className={`text-xs font-bold truncate uppercase ${
                                    isCurrentLesson ? 'text-[#0FFF55]' : 'text-gray-200'
                                  }`}
                                >
                                  {les.title}
                                </h5>
                                <span className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5 font-medium">
                                  <Clock className="w-3 h-3 text-gray-500" />
                                  {les.durationText}
                                </span>
                              </div>
                            </div>

                            {/* Checkmark Completion Status */}
                            {les.isCompleted && (
                              <Check className="w-4 h-4 text-[#0FFF55] flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
